import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import { SafetyNavIcon } from 'assets/icons';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardEnableCard from 'components/foundation/dashboard/DashboardEnableCard/DashboardEnableCard';
import DashboardSingleRibbon from 'components/foundation/dashboard/DashboardSingleRibbon/DashboardSingleRibbon';
import { useActiveMember, useOpenable } from 'hooks';
import { t } from 'i18next';
import { useFeatureFlags } from 'utils/features';
import './PendingSafetyTasksDashboardCard.scss';
import usePendingSafetyTasksData from 'containers/dashboard/hooks/usePendingSafetyTasksData';
import useNavigation from 'hooks/navigation/useNavigation';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import DashboardSpinnerCard from 'components/foundation/dashboard/DashboardSpinnerCard/DashboardSpinnerCard';
import SafetyTrialDialog from './SafetyTrialDialog/SafetyTrialDialog';
import useSafetyIncidentsEnabled from 'hooks/feature/safety/useSafetyIncidentsEnabled/useSafetyIncidentsEnabled';
import useSafetyObservationsEnabled from 'hooks/feature/safety/useSafetyObservationsEnabled/useSafetyObservationsEnabled';
import useDashboardAnalytics from 'containers/dashboard/hooks/useDashboardAnalytics';

export interface IPendingSafetyTasksDashboardCardProps {
  onHide?: () => void;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  className?: ClassName;
}

const PendingSafetyTasksDashboardCard = (props: IPendingSafetyTasksDashboardCardProps) => {
  const { className, theme, onHide } = props;

  const activeMember = useActiveMember();
  const memberSettings = useMemberSettings();
  const isSafetyIncidentsEnabled = useSafetyIncidentsEnabled();
  const isSafetyObservationsEnabled = useSafetyObservationsEnabled();
  const hasTrialedSafety = useFeatureFlags('HAS_TRIALED_SAFETY');
  const aggregatedInfo = usePendingSafetyTasksData();
  const safetyReportsDialogOpenable = useOpenable();
  const freeTrialDialogOpenable = useOpenable();
  const navigateToOverview = useNavigation('/safety/overview');
  const userEvents = useDashboardAnalytics();

  const getPendingTasksTotal = () => {
    let total = 0;
    if (
      !(
        memberSettings?.web?.ui?.dashboard?.dashboardGridSettings?.pendingSafetyTasks?.options?.openObservations ===
        false
      ) &&
      aggregatedInfo?.openObservations
    ) {
      total += aggregatedInfo.openObservations;
    }
    if (
      !(
        memberSettings?.web?.ui?.dashboard?.dashboardGridSettings?.pendingSafetyTasks?.options?.openIncidents === false
      ) &&
      aggregatedInfo?.openIncidents
    ) {
      total += aggregatedInfo.openIncidents;
    }
    if (
      !(
        memberSettings?.web?.ui?.dashboard?.dashboardGridSettings?.pendingSafetyTasks?.options
          ?.incidentsNeedingReview === false
      ) &&
      aggregatedInfo?.incidentsNeedingReview
    ) {
      total += aggregatedInfo.incidentsNeedingReview;
    }
    return total;
  };

  const handleNavigateToOverviewClick = () => {
    navigateToOverview();
    userEvents.recordPendingSafetyTasksClick();
  };

  const classes = classNames('pending-safety-tasks-dashboard-card', className);

  return (
    <>
      {isSafetyIncidentsEnabled || isSafetyObservationsEnabled ? (
        <div className={classes}>
          <DashboardSingleRibbon theme={Theme.DARK} onClick={handleNavigateToOverviewClick}>
            <>
              <h1 className={classNames('mb-5', 'single-ribbon-value')}>{getPendingTasksTotal()}</h1>
              <div className={'single-ribbon-title'}>{t('Pending Safety Tasks')}</div>
            </>
          </DashboardSingleRibbon>
        </div>
      ) : activeMember.position?.manageCompanySettings ? (
        hasTrialedSafety ? (
          <DashboardEnableCard
            title={t('Safety Bundle')}
            svg={SafetyNavIcon}
            buttonLabel={t('Upgrade')}
            onClick={safetyReportsDialogOpenable.open}
            theme={theme}
            onHide={onHide}
          />
        ) : (
          <DashboardEnableCard
            title={t('Safety Bundle')}
            svg={SafetyNavIcon}
            buttonLabel={t('Start Free Trial')}
            onClick={freeTrialDialogOpenable.open}
            theme={theme}
            onHide={onHide}
          />
        )
      ) : (
        <DashboardSpinnerCard theme={Theme.DARK} />
      )}
      <SafetyTrialDialog isOpen={freeTrialDialogOpenable.isOpen} onClose={freeTrialDialogOpenable.close} />
    </>
  );
};

export default PendingSafetyTasksDashboardCard;
